import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './admin-add-user-assign-accounts.scss';
import { DataTable, Form } from '@ampol/design-system';
import { getAccountLevelName } from 'utilities/core-utilities';
import { getAccountsForUser, getAccountsForUserReset } from 'actions/accountActions';
import { getAssignAccountsUserMap } from 'utilities/user-utilities';

import SearchInput from 'components/search-input/search-input';
import ContentControls from 'components/content-controls/content-controls';
import * as yup from 'yup';
import Env from 'Env';
import Regex from 'constants/regex';
import Validation from 'constants/validation-messages';

/**
 * Admin Portal - Add New User - Assign Accounts Step
 * @returns JSX Element
 */
const AdminAddUserAssignAccounts = ({ formId, onSubmit, setAdminAssignAccountsSubmitButtonDisabled }) => {
  const dispatch = useDispatch();
  const assignRolesList = useSelector((state) => state.assignRolesList.roles);

  const getAccountsForUserLoading = useSelector((state) => state.accountsStore.getAccountsForUserLoading);
  const getAccountsForUserResult = useSelector((state) => state.accountsStore.getAccountsForUserResult);
  const getAccountsForUserError = useSelector((state) => state.accountsStore.getAccountsForUserError);

  const [isFormValid, setIsFormValid] = useState(false);
  const [sortColumn, setSortColumn] = useState('accountNumber');
  const [sortDirection, setSortDirection] = useState('desc');
  const [accountNumber, setAccountNumber] = useState(null);

  const [parentAccount, setParentAccount] = useState(null);
  const [grandParentAccount, setGrandParentAccount] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 5;
  const totalPages = Math.ceil(getAccountsForUserResult?.resultHierarchySize / pageSize);

  const validAccountRegex = Env.isAmpolCashEnabled() ? Regex.ACCOUNT_NUMBER : Regex.CARD_ACCOUNT;

  const searchSchema = yup.object().shape({
    searchTerm: yup
      .string()
      .nullable()
      .matches(validAccountRegex, { excludeEmptyString: true, message: Validation.ACCOUNT_NUMBER_TEN_DIGITS })
      .test('searchTerm', Validation.ACCOUNT_NUMBER_NOT_FOUND, () => !getAccountsForUserError),
  });

  useEffect(() => {
    if (accountNumber) {
      dispatch(getAccountsForUser('new-user', accountNumber, pageSize, pageNumber, sortColumn, sortDirection));
    }
  }, [accountNumber, pageNumber, sortColumn, sortDirection]);

  useEffect(() => {
    if (getAccountsForUserResult) {
      const grandParent = getAccountsForUserResult?.ancestors.find((x) => x.level === 'A');
      const parent = getAccountsForUserResult?.ancestors.find((x) => x.level === 'B');

      setGrandParentAccount(grandParent);
      setParentAccount(parent);
      setSelectedRole(selectedRole);
    } else {
      setGrandParentAccount(null);
      setParentAccount(null);
    }
  }, [getAccountsForUserResult]);

  useEffect(() => {
    const assignButtonEnabled = selectedRole && getAccountsForUserResult?.result;

    setIsFormValid(assignButtonEnabled);
    setAdminAssignAccountsSubmitButtonDisabled(!assignButtonEnabled);
  }, [selectedRole, getAccountsForUserResult]);

  const onSearch = (value) => {
    if (value.match(validAccountRegex)) {
      setPageNumber(1);
      setAccountNumber(value);
    } else {
      setAccountNumber(null);
      setSelectedRole(null);
      dispatch(getAccountsForUserReset());
    }
  };

  const onRoleChange = (values) => {
    setSelectedRole(values.role);
  };

  const onPageNumberChange = (value) => {
    setPageNumber(value);
  };

  const onTableColumnSorted = (column, isAscending) => {
    setSortColumn(column);
    setSortDirection(isAscending ? 'asc' : 'desc');
  };

  const rowClassCondition = (row) => {
    return row.level === 'A'
      ? 'admin-add-user-assign-accounts__row-grand-parent'
      : row.level === 'B'
        ? 'admin-add-user-assign-accounts__row-parent'
        : '';
  };

  const onAssignAccountsSubmit = () => {
    if (!isFormValid) {
      return;
    }

    const values = {
      grandParentCustomerNumber: grandParentAccount
        ? grandParentAccount.accountNumber
        : parentAccount
          ? parentAccount.accountNumber
          : getAccountsForUserResult.result.accountNumber,
      roleId: selectedRole,
      assignedAccounts: getAssignAccountsUserMap(getAccountsForUserResult.result, grandParentAccount, parentAccount),
    };

    onSubmit(values);
  };

  return (
    <div className='admin-add-user-assign-accounts'>
      <div className='admin-add-user-assign-accounts__account-form'>
        <div className='admin-add-user-assign-accounts__search-input'>
          <SearchInput
            onSearch={onSearch}
            searchSchema={searchSchema}
            triggerValidation={getAccountsForUserError}
            className={'custom-width'}
          />
        </div>
        <div className='admin-add-user-assign-accounts__role-dropdown'>
          <Form
            id={formId}
            onSubmit={onAssignAccountsSubmit}
            onChange={onRoleChange}
            defaultValues={{ role: selectedRole }}
            schema={{}}
          >
            <Form.DropDown
              name='role'
              label='Security role'
              placeHolder='Select role'
              options={assignRolesList.map((x) => ({ label: x.name, value: x.id }))}
              ariaLabel={'Select role'}
              disabled={!getAccountsForUserResult}
            />
          </Form>
        </div>
      </div>

      {totalPages > 1 && (
        <ContentControls
          showPageControls={true}
          pageNumber={pageNumber}
          pageTotal={totalPages}
          onPageChange={onPageNumberChange}
          itemCount={getAccountsForUserResult?.resultHierarchySize}
        />
      )}

      <div className={'admin-add-user-assign-accounts__table-container'}>
        <DataTable
          noDataMessage={'Search by account number to show results'}
          caption={'Assign Portal Accounts'}
          columns={[
            {
              header: 'Account Number',
              accessorKey: 'accountNumber',
              defaultSorted: 'desc',
            },
            {
              header: 'Account Name',
              accessorKey: 'accountName',
            },
            {
              header: 'Level',
              id: 'accountLevel',
              accessorFn: (x) => getAccountLevelName(x.level),
              disableSort: true,
            },
          ]}
          isSortable={true}
          onColumnSorted={onTableColumnSorted}
          isLoading={getAccountsForUserLoading}
          columnTemplate={'1fr 1fr 1fr'}
          data={getAccountsForUserResult?.resultHierarchy}
          tableVariant='list'
          loadingRowCount={pageSize}
          rowClassFn={(row) => rowClassCondition(row)}
          isSelectable={true}
          rowSelectionDisabledFn={() => true}
          rowSelectedFn={() => true}
        />
      </div>
    </div>
  );
};

export default AdminAddUserAssignAccounts;
